import React, {
  useCallback,
  ChangeEvent,
}                             from 'react';
import styled                 from 'styled-components';
import {
  FormikHelpers,
  FieldInputProps,
}                             from 'formik';
import {
  Select as MuiSelect,
  MenuItem,
  FormControl,
  SelectProps,
  CircularProgress,
}                             from '@material-ui/core';
import {
  Check,
  ExpandLess,
  ExpandMore,
}                             from '@material-ui/icons';
import { makeStyles }         from '@material-ui/core/styles';
import {
  ISelectOption,
  TTextAlignProp,
  TSelectOnChange,
}                             from '@typings/components';

const StyledSelect = styled(MuiSelect)`
  && {
    width: ${({ width }: any): string => width};

    .MuiSelect-root {
      &:focus{
        background-color: ${({ theme: { backgroundColor } }): string => backgroundColor.white};
      }
    }

    .MuiSelect-selectMenu {
      display: flex;

      ${({ align }: any): false | string => align === 'left'   && 'justify-content: center;'};
      ${({ align }: any): false | string => align === 'center' && 'justify-content: center;'};
      ${({ align }: any): false | string => align === 'right'  && 'justify-content: flex-end;'};

      & span {
        width         : 175px;
        white-space   : nowrap;
        overflow      : hidden;
        text-overflow : ellipsis;
      }

      svg:first-child {
        display: none;
      }
    }
  }
` as any;

const StyledMenuItem = styled(MenuItem)`
  && {
    display  : flex;
    position : relative;

    span {
      padding-right : 14px;
      white-space   : normal;
    }

    ${({ align }: any): false | string => align === 'left'   && 'padding-left: 25%;'};
    ${({ align }: any): false | string => align === 'center' && 'justify-content: center;'};
    ${({ align }: any): false | string => align === 'right'  && 'justify-content: flex-end;'};

    svg:first-child {
      position : absolute;
      left     : 14px;
      color    : ${({ theme: { textColor } }): string => textColor.secondary};
    }

    svg:not(:first-child) {
      position : absolute;
      right    : 4px;
    }
  }
` as any;

const useStyles = makeStyles({
  popper: {
    border                 : '1px solid #aeaeae',
    '&::-webkit-scrollbar' : {
      display: 'none',
    },
  },
});

const LoaderWrapper = styled.div`
  width           : 100%;
  display         : flex;
  justify-content : center;
`;

interface ISelectProps extends SelectProps {
  form?      : FormikHelpers<any>;
  field?     : FieldInputProps<any>;
  align?     : TTextAlignProp;
  width?     : string;
  options    : ISelectOption[];
  noneText?  : string;
  isLoading? : boolean;
}

export const Select: React.FC<ISelectProps> = ({
  form,
  field,
  align,
  width,
  options,
  noneText,
  onChange,
  isLoading,
  ...props
}) => {
  const classes = useStyles();

  const handleChange = useCallback<TSelectOnChange>(
    (event: ChangeEvent<HTMLSelectElement>) => {
      if (!form || !field) return;

      form.setFieldValue(field.name, event.target.value);
    },
    [form, field],
  );

  return (
    <FormControl
      margin  = "dense"
      variant = "outlined"
    >
      <StyledSelect
        {...field}
        {...props}
        align         = {align}
        width         = {width}
        onChange      = {onChange || handleChange}
        IconComponent = {ExpandMore}
        MenuProps     = {{
          PopoverClasses     : { paper: classes.popper },
          elevation          : 0,
          getContentAnchorEl : null,
          anchorOrigin       : {
            vertical   : 'bottom',
            horizontal : 'center',
          },
          transformOrigin: {
            vertical   : 'top',
            horizontal : 'center',
          },
        }}
      >
        {noneText && (
          <StyledMenuItem aria-label={noneText} value="" align={align}>
            <span>{noneText}</span>
            <ExpandLess />
          </StyledMenuItem>
        )}
        {isLoading ? <LoaderWrapper><CircularProgress /></LoaderWrapper> : options.map(
          (option: ISelectOption): JSX.Element => (
            <StyledMenuItem
              key        = {option?.label}
              value      = {option?.value ?? ''}
              disabled   = {option?.disabled}
              aria-label = {option?.label}
              align      = {align}
            >
              {(props?.value === option?.value
               || field?.value === option?.value)
                && <Check />}
              <span>{option?.label}</span>
            </StyledMenuItem>
          ),
        )}
      </StyledSelect>
    </FormControl>
  );
};

Select.defaultProps = {
  align    : 'left',
  width    : '220px',
  noneText : '',
};
